import { RefObject, useEffect, useState } from 'react';

import { FIXED_HEADER_CONTAINER_ID } from '@/components/Header/HeaderBase';

/**
 * Matches the `h-12` height applied to the discovery table header cells.
 * Used to determine when the sticky header should stop being visible as the
 * table scrolls off the top of the viewport.
 */
const STICKY_HEADER_HEIGHT = 48;

type Params = {
  triggerRef: RefObject<HTMLDivElement | null>;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  stickyHeaderRef: RefObject<HTMLDivElement | null>;
  stickyScrollContainerRef: RefObject<HTMLDivElement | null>;
  /**
   * Set to `false` to skip wiring up the sticky header listeners entirely.
   * Used by callers (like the Solana page) that opt out of rendering the
   * overlay and therefore don't need the scroll/resize observers running.
   * Defaults to `true`.
   */
  enabled?: boolean;
};

/**
 * Controls the homepage discovery table's sticky header overlay.
 *
 * Returns the measured top offset of the site's fixed header, which callers
 * apply as the `top` style of the sticky overlay so it sits flush below the
 * announcement banner and global nav.
 *
 * Visibility is toggled by writing `data-visible` directly onto the overlay
 * element so updates happen in the same frame as the scroll event, avoiding
 * the latency of a React state round-trip while the user scrolls quickly.
 *
 * Column widths are mirrored from the main table onto the sticky overlay so
 * they stay perfectly aligned; without this the sticky table's `<thead>`-only
 * layout picks narrower widths than the main table (which sizes columns to
 * fit both header and data cells) and the columns appear shifted.
 */
export const useDiscoveryStickyHeader = ({
  triggerRef,
  scrollContainerRef,
  stickyHeaderRef,
  stickyScrollContainerRef,
  enabled = true,
}: Params) => {
  const [topOffset, setTopOffset] = useState(0);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const fixedHeader = document.getElementById(FIXED_HEADER_CONTAINER_ID);

    if (!fixedHeader) {
      return;
    }

    const updateTopOffset = () => {
      setTopOffset(fixedHeader.getBoundingClientRect().height);
    };

    updateTopOffset();

    const resizeObserver = new ResizeObserver(updateTopOffset);
    resizeObserver.observe(fixedHeader);
    window.addEventListener('resize', updateTopOffset, { passive: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateTopOffset);
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const stickyHeader = stickyHeaderRef.current;
    const trigger = triggerRef.current;
    const scrollContainer = scrollContainerRef.current;

    if (topOffset === 0 || !stickyHeader || !trigger || !scrollContainer) {
      return;
    }

    let lastIsVisible: boolean | null = null;

    const syncColumnWidths = () => {
      const stickyScrollContainer = stickyScrollContainerRef.current;

      if (!stickyScrollContainer) {
        return;
      }

      const mainHeaderCells = scrollContainer.querySelectorAll<HTMLTableCellElement>('thead th');
      const stickyHeaderCells =
        stickyScrollContainer.querySelectorAll<HTMLTableCellElement>('thead th');

      mainHeaderCells.forEach((mainCell, index) => {
        const stickyCell = stickyHeaderCells[index];

        if (stickyCell) {
          stickyCell.style.width = `${mainCell.getBoundingClientRect().width}px`;
        }
      });
    };

    const applyVisibility = () => {
      const triggerTop = trigger.getBoundingClientRect().top;
      const tableBottom = scrollContainer.getBoundingClientRect().bottom;
      const isVisible = triggerTop <= topOffset && tableBottom > topOffset + STICKY_HEADER_HEIGHT;

      if (isVisible === lastIsVisible) {
        return;
      }

      lastIsVisible = isVisible;
      stickyHeader.dataset.visible = isVisible ? 'true' : 'false';
      stickyHeader.setAttribute('aria-hidden', String(!isVisible));

      if (isVisible) {
        syncColumnWidths();

        const stickyScrollContainer = stickyScrollContainerRef.current;

        if (stickyScrollContainer) {
          stickyScrollContainer.scrollLeft = scrollContainer.scrollLeft;
        }
      }
    };

    const handleLayoutChange = () => {
      applyVisibility();
      syncColumnWidths();
    };

    applyVisibility();
    syncColumnWidths();

    const resizeObserver = new ResizeObserver(handleLayoutChange);
    resizeObserver.observe(trigger);
    resizeObserver.observe(scrollContainer);
    window.addEventListener('scroll', applyVisibility, { passive: true });
    window.addEventListener('resize', handleLayoutChange, { passive: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('scroll', applyVisibility);
      window.removeEventListener('resize', handleLayoutChange);
    };
  }, [
    enabled,
    topOffset,
    triggerRef,
    scrollContainerRef,
    stickyHeaderRef,
    stickyScrollContainerRef,
  ]);

  return { topOffset };
};
